import React, { createContext, useState, useContext, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  reload
} from 'firebase/auth';
import { auth, db, googleProvider, facebookProvider } from '../config/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';

// Se exporta para poder inyectar una sesión falsa desde la ruta de pruebas de
// desarrollo (pages/DevOnboarding.jsx) y así abrir pantallas con login sin
// tener que loguearse de verdad. La app siempre usa AuthProvider.
export const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up function
  const signup = async (email, password, businessName, timeZone) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Enviar email de verificación
      await sendEmailVerification(user);
      
      // Guardar documento del negocio en Firestore con zona horaria
      await setDoc(doc(db, 'businesses', user.uid), {
        email: user.email,
        name: businessName,
        onboardingCompleted: false,
        createdAt: new Date().toISOString(),
        timeZone: timeZone || 'America/Argentina/Buenos_Aires'
      });
      
      return userCredential;
    } catch (error) {
      throw error;
    }
  };

  // Login function with MFA support
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential;
    } catch (error) {
      // Handle MFA error
      if (error.code === 'auth/multi-factor-auth-required') {
        // Se relanza un Error de verdad y no un objeto pelado: tirar `{...}`
        // pierde el stack y hace que cualquier `instanceof Error` de arriba dé
        // false, así que el error viaja sin la información para diagnosticarlo.
        const mfaError = new Error(error.message || 'Se requiere segundo factor');
        Object.assign(mfaError, error, {
          type: 'MFA_REQUIRED',
          resolver: error.resolver
        });
        throw mfaError;
      }
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('isAuthenticated');
    } catch (error) {
      throw error;
    }
  };

  // Google login function
  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      // Detectar zona horaria del navegador
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Argentina/Buenos_Aires';
      // Check if user document exists, if not create it
      const userDocRef = doc(db, 'businesses', user.uid);
      const docSnap = await getDoc(userDocRef);
      if (!docSnap.exists()) {
        await setDoc(userDocRef, {
          email: user.email,
          name: user.displayName || 'Mi Negocio',
          onboardingCompleted: false,
          createdAt: new Date().toISOString(),
          provider: 'google',
          timeZone: timeZone
        });
      }
      return result;
    } catch (error) {
      throw error;
    }
  };

  // Facebook login function
  const signInWithFacebook = async () => {
    try {
      const result = await signInWithPopup(auth, facebookProvider);
      const user = result.user;
      // Detectar zona horaria del navegador
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Argentina/Buenos_Aires';
      // Check if user document exists, if not create it
      const userDocRef = doc(db, 'businesses', user.uid);
      const docSnap = await getDoc(userDocRef);
      if (!docSnap.exists()) {
        await setDoc(userDocRef, {
          email: user.email,
          name: user.displayName || 'Mi Negocio',
          onboardingCompleted: false,
          createdAt: new Date().toISOString(),
          provider: 'facebook',
          timeZone: timeZone
        });
      }
      return result;
    } catch (error) {
      throw error;
    }
  };

  // Reenviar email de verificación
  const resendEmailVerification = async () => {
    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
      }
    } catch (error) {
      throw error;
    }
  };

  // Refrescar usuario para obtener el estado actualizado de emailVerified
  const refreshCurrentUser = async () => {
    try {
      if (auth.currentUser) {
        await reload(auth.currentUser);
        setCurrentUser(auth.currentUser);
      }
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    console.log('🔐 AuthContext: Setting up auth state listener');
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔐 AuthContext: Auth state changed, user:', user ? `${user.email} (${user.uid})` : 'null');

      // `loading` vuelve a true en CADA cambio de sesión, no sólo en el primero.
      //
      // Sin esto, al iniciar sesión aparecía el onboarding por un instante y
      // recién después el panel. La secuencia era:
      //
      //   1. Con la pantalla de login abierta, el listener ya había corrido una
      //      vez con `user = null` y había dejado `loading` en false.
      //   2. Al loguearse, el listener corre de nuevo: `setCurrentUser(user)` se
      //      aplica ya, pero el `await getDoc` de abajo suspende la función, así
      //      que React pinta con `currentUser` puesto, `userData` todavía en
      //      null y `loading` en false, que nadie volvió a levantar.
      //   3. En ese render, tanto ProtectedRoute como el efecto de Login leían
      //      "hay usuario y no hay onboardingCompleted" y mandaban a
      //      /admin/onboarding.
      //   4. Llegaba el getDoc con onboardingCompleted: true y rebotaba al
      //      dashboard.
      //
      // O sea que el onboarding no se mostraba por error de ruteo: se mostraba
      // porque durante ~un viaje a Firestore la sesión se veía a medio cargar y
      // no había forma de distinguir "todavía no sé" de "no completó nada".
      // Ahora `loading` cubre auth Y los datos de Firestore, que es lo que todos
      // los consumidores ya asumían (ver el comentario de ProtectedRoute).
      setLoading(true);
      setCurrentUser(user);

      if (user) {
        try {
          // Intentar obtener datos del usuario desde Firestore
          try {
            const businessRef = doc(db, 'businesses', user.uid);
            const businessDoc = await getDoc(businessRef);
            
            if (businessDoc.exists()) {
              const businessData = businessDoc.data();
              setUserData(businessData);
            } else {
              setUserData({ onboardingCompleted: false });
            }
            
          } catch (error) {
            // Si es un error de permisos, intentamos una estrategia alternativa
            if (error.code === 'permission-denied') {
              // Intentar verificar con el backend si el usuario ya completó onboarding
              try {
                const token = await user.getIdToken();
                
                const response = await fetch('http://localhost:3001/api/user/status', {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                });
                
                if (response.ok) {
                  const userData = await response.json();
                  setUserData(userData);
                } else {
                  setUserData({ onboardingCompleted: false });
                }
              } catch (backendError) {
                setUserData({ onboardingCompleted: false });
              }
            } else {
              // Para otros errores, tratamos como usuario nuevo
              setUserData({ onboardingCompleted: false });
            }
          }
        } catch (error) {
          // Si es un error de permisos, intentamos una estrategia alternativa
          if (error.code === 'permission-denied') {
            // Intentar verificar con el backend si el usuario ya completó onboarding
            try {
              const token = await user.getIdToken();
              const response = await fetch('http://localhost:3001/api/user/status', {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (response.ok) {
                const userData = await response.json();
                setUserData(userData);
              } else {
                setUserData({ onboardingCompleted: false });
              }
            } catch (backendError) {
              setUserData({ onboardingCompleted: false });
            }
          } else {
            // Para otros errores, tratamos como usuario nuevo
            setUserData({ onboardingCompleted: false });
          }
        }
      } else {
        console.log('🔐 AuthContext: No user, setting userData to null');
        setUserData(null);
      }
      console.log('🔐 AuthContext: Setting loading to false');
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  /**
   * Manda el mail de recuperación de contraseña.
   *
   * Firebase se encarga del link y de la pantalla para elegir la contraseña
   * nueva, así que acá no hay token propio que manejar. El dominio desde el que
   * sale el mail se configura en la consola de Firebase (Authentication →
   * Templates), no en el código.
   */
  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  const refreshUserData = async () => {
    const user = auth.currentUser;
    if (user) {
      try {
        const userDocRef = doc(db, 'businesses', user.uid);
        const docSnap = await getDoc(userDocRef);
        if (docSnap.exists()) {
          setUserData(docSnap.data());
        }
      } catch (error) {
        console.warn('Error refreshing user data from Firestore:', error);
        // En caso de error, mantenemos los datos actuales
      }
    }
  };

  const value = {
    currentUser, // El objeto de Firebase con los métodos
    userData,    // El objeto de Firestore con los datos del negocio
    loading,
    signup,
    login,
    logout,
    signInWithGoogle,
    signInWithFacebook,
    resendEmailVerification,
    resetPassword,
    refreshCurrentUser,
    refreshUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 